import styled from "styled-components";
import isEqual from "lodash/isEqual";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DEFAULT_GOOGLE_MAP_CENTER } from "dash-common";
import {
  GoogleMap,
  useGoogleApiLoader,
  Spinner,
  FilterContext,
  SearchContext,
  Box,
  Flex,
  ViewerContext,
  googleZoomLevelToMiles,
} from "dash-ui";
import { GetPropertyByIdQuery } from "gql/generated.graphql";

import MarkersCluster from "containers/properties/AllPropertiesListContainer/components/PropertiesContentView/components/PropertiesMapView/components/MarkersCluster/MarkersCluster";
import {
  formatPropertiesFilter,
  PropertiesFilter,
} from "containers/properties/AllPropertiesListContainer/components/PropertiesContentView/components/PropertiesListView/PropertiesListView.helpers";
import {
  formatPropertiesMapLocationFilter,
  getQueryBounds,
  RADIUS,
} from "containers/properties/AllPropertiesListContainer/components/PropertiesContentView/components/PropertiesMapView/PropertiesMapView.helpers";
import { useRestCall } from "common/hooks/useRestCall/useRestCall";

const minZoomLevel = 5;
const maxZoomLevel = 18;

const SpinnerWrapper = styled(Flex)`
  bottom: 0;
  z-index: 3;
`;

/** !important to reduce count of refetch query calls (each time when map changes),
 *  we have two bounds (area on map) QueryBounds and MapBounds
 *    QueryBounds - its circle with certain radius
 *    MapBounds - its rectangle viewed map area
 *  if QueryBounds contains MapBounds (circle > rectangle) we use QueryBounds for refetch query
 *  if MapBounds contains QueryBounds (circle < rectangle) we use MapBounds for refetch query
 *  + we use debounce query
 *  */

const PropertiesMapView = () => {
  const isReady = useGoogleApiLoader();
  const mapRef = useRef<google.maps.Map | null>(null);

  const { state: filterState, setFilterState } = useContext(FilterContext);
  const { searchSlug } = useContext(SearchContext);
  const { broker } = useContext(ViewerContext);
  const { center: filterCenter, radius: filterRadius } = filterState;
  const searchText = searchSlug.length > 2 ? searchSlug : null;

  const [mapUpdatedTineStamp, setMapUpdatedTimeStamp] = useState(Date.now());
  const queryBoundsRef = useRef<google.maps.LatLngBounds | null | undefined>(
    null
  );
  const mapVariablesRef = useRef<Record<string, any> | null>(null);

  const filterVariables = useMemo(
    () => ({ ...formatPropertiesFilter(filterState), searchText }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterState, searchSlug]
  );

  const [propertiesFilter, setPropertiesFilter] =
    useState<PropertiesFilter | null>(null);
  const { data: responseData, loading } = useRestCall({
    endpoint: "internal-properties",
    method: "POST",
    body: {
      filter: {
        ...propertiesFilter,
      },
      pagination: { allowLimit: false },
    },
    skip: propertiesFilter === null,
  });

  const { properties } = useMemo(
    () => ({
      properties: responseData?.collection || [],
    }),
    [responseData]
  );

  /** filter properties that inside MapBounds
   * (we might have more properties that in MapBounds when QueryBounds > MapBounds ) */
  const mapProperties = useMemo(() => {
    const zoomLevel = mapRef.current?.getZoom();
    const lat = mapRef.current?.getCenter()?.lat() || 0;
    const mapRadius =
      zoomLevel && lat ? googleZoomLevelToMiles(lat, zoomLevel) : 0;
    if (mapRadius > RADIUS) return properties;

    const mapBounds = mapRef.current?.getBounds();
    return mapBounds
      ? properties.filter((property) => {
          const [lng, lat] = property?.location?.geoLocation?.coordinates || [];
          return mapBounds.contains(new google.maps.LatLng(lat, lng));
        })
      : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties, mapUpdatedTineStamp]);

  /** get map center effect */
  const { center } = useMemo(() => {
    const [lng, lat] =
      broker?.preferredPropertySettings?.location?.geoLocation?.coordinates ||
      [];
    const settingsCenter = lat || lng ? { lat, lng } : null;
    const center = filterCenter || settingsCenter || DEFAULT_GOOGLE_MAP_CENTER;
    return { center };
  }, [broker, filterCenter]);

  const { lat } = center;
  /** get map zoomLevel effect */
  const zooLevel = useMemo(() => {
    const radius =
      filterRadius || broker?.preferredPropertySettings?.radiusInMiles;
    if (!radius) {
      return undefined;
    }
    const zoomInMiles = Array.from({ length: maxZoomLevel + 1 })
      .slice(minZoomLevel)
      .map((_, zoom) => googleZoomLevelToMiles(lat, zoom + minZoomLevel) / 2);
    /** return closest zomLevel to filter radius */
    const closest = zoomInMiles.reduce((prev, curr) =>
      Math.abs(curr - radius) < Math.abs(prev - radius) ? curr : prev
    );
    const zoomInMilesIndex = zoomInMiles.findIndex(
      (value) => value === closest
    );
    return zoomInMilesIndex + minZoomLevel - 2;
  }, [lat, filterRadius, broker]);

  const onDragEnd = useCallback(() => {
    if (mapRef.current) {
      queryBoundsRef.current = getQueryBounds(
        mapRef.current,
        queryBoundsRef.current
      );
      setMapUpdatedTimeStamp(Date.now());
    }
    // FIXME: re-check next line
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFilterState, mapRef.current]);

  const onZoomChanged = useCallback(() => {
    if (mapRef.current) {
      queryBoundsRef.current = getQueryBounds(
        mapRef.current,
        queryBoundsRef.current
      );
      setMapUpdatedTimeStamp(Date.now());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFilterState, mapRef.current]);

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      setFilterState("center", {
        lat: map.getCenter()?.lat(),
        lng: map.getCenter()?.lng(),
      });
      // next step is in onBoundsChanged
    },
    [setFilterState]
  );

  /** When onLoad call's bounds not ready yet, to get bound when map is ready we use onBoundsChanged */
  const onBoundsChanged = useCallback(() => {
    if (!queryBoundsRef.current) {
      queryBoundsRef.current = getQueryBounds(
        mapRef.current,
        queryBoundsRef.current
      );
      setMapUpdatedTimeStamp(Date.now());
    }
  }, [setMapUpdatedTimeStamp]);

  /** refetch effect */
  useEffect(() => {
    if (queryBoundsRef.current) {
      const mapVariables = formatPropertiesMapLocationFilter({
        ...filterState,
        bounds: queryBoundsRef.current?.toJSON(),
        center: {
          lat: queryBoundsRef.current.getCenter()?.lat(),
          lng: queryBoundsRef.current.getCenter()?.lng(),
        },
      });
      const mapFilter = {
        ...filterVariables,
        ...mapVariables,
      };
      /** !important we refetch only if filter variables really changed */
      const isChanged = !isEqual(mapFilter, mapVariablesRef.current);
      if (!loading && isChanged) {
        setPropertiesFilter(mapFilter);
      }
      mapVariablesRef.current = mapFilter;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapUpdatedTineStamp, filterVariables, loading]);

  return (
    <Box pos="relative" w="100%" h="100%" minHeight={550}>
      {isReady && (
        <GoogleMap
          h="100%"
          center={center}
          onDragEnd={onDragEnd}
          onZoomChanged={onZoomChanged}
          onBoundsChanged={onBoundsChanged}
          onLoad={onLoad}
          zoom={zooLevel}
          options={{
            minZoom: minZoomLevel,
            maxZoom: maxZoomLevel,
          }}
        >
          <>
            {/*{isOverlayVisible && (*/}
            {/*  <Overlay justifyContent="center" alignItems="center">*/}
            {/*    <Flex flexDirection="column" maxWidth={380} alignItems="center">*/}
            {/*      <Button mb={2} onClick={setFilterStateLocation}>*/}
            {/*        {t('propertyPage.goToTheClosestProperty')}*/}
            {/*      </Button>*/}
            {/*      <StyledText color={theme.colors.white}>*/}
            {/*        {t('propertyPage.noPropertiesWereFoundOnMap')}*/}
            {/*      </StyledText>*/}
            {/*    </Flex>*/}
            {/*  </Overlay>*/}
            {/*)}*/}
            {mapProperties.length && (
              <MarkersCluster
                loading={loading}
                properties={
                  mapProperties as GetPropertyByIdQuery["getPropertyById"][]
                }
              />
            )}
          </>
        </GoogleMap>
      )}

      {loading && (
        <SpinnerWrapper
          pos="absolute"
          alignItems="flex-end"
          justifyContent="center"
          mb={4}
          w="100%"
        >
          <Spinner />
        </SpinnerWrapper>
      )}

      {!isReady && <Spinner fullContent />}
    </Box>
  );
};

export default PropertiesMapView;
